import { useState, useEffect, useRef } from 'react';
import styles from './widget.module.css';
import { v4 as uuidv4 } from 'uuid'; 
import { motion, AnimatePresence } from 'framer-motion'; 
import { 
  AiOutlineLoading3Quarters, 
  AiOutlineSend, 
  AiOutlineMessage, 
  AiOutlineClose, 
  AiOutlineBulb, 
  AiOutlineMoon 
} from 'react-icons/ai'; 
import { FaUserCircle, FaRobot } from 'react-icons/fa'; 
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css'; 

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false); 
  const [messages, setMessages] = useState([]); 
  const [input, setInput] = useState(''); 
  const [sessionId, setSessionId] = useState(''); 
  const [loading, setLoading] = useState(false); 
  const [darkMode, setDarkMode] = useState(true); 
  const messagesEndRef = useRef(null);
  const hasInitialMessagesRef = useRef(false); 

  useEffect(() => {
    let storedSessionId = localStorage.getItem('session_id');
    if (!storedSessionId) {
      storedSessionId = uuidv4(); 
      localStorage.setItem('session_id', storedSessionId);
    }
    setSessionId(storedSessionId);
  }, []);

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();

      if (!hasInitialMessagesRef.current) {
        addInitialMessages();
        hasInitialMessagesRef.current = true;
      }
    }
  }, [messages, isOpen]);

  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
    }
  }, [darkMode]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const addInitialMessages = () => {
    setLoading(true);

    const welcomeMessage = {
      id: uuidv4(),
      sender: 'ai',
      text: 'Welcome to Shivam\'s Portfolio! 👋 I am NeXuS-AI you can ask all questions related to Shivam. 🚀',
      timestamp: new Date(),
    };
    

    const followUpMessage = {
      id: uuidv4(),
      sender: 'ai',
      text: 'Hi, How can I help you?',
      timestamp: new Date(),
    };

    setTimeout(() => {
      setMessages([welcomeMessage]);
      setLoading(false);

      setTimeout(() => {
        setLoading(true);
        setTimeout(() => {
          setMessages((prev) => [...prev, followUpMessage]);
          setLoading(false);
        }, 1000); 
      }, 500); 
    }, 1000); 
  };

  const sendMessage = async () => {
    if (input.trim() === '') return;

    const userMessage = { 
      id: uuidv4(),
      sender: 'user', 
      text: input,
      timestamp: new Date(),
    };
    setMessages([...messages, userMessage]);
    setInput('');
    setLoading(true); 

    try {
      const response = await fetch('https://v1-chat.itshivam.me/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ session_id: sessionId, question: input }),
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();
      const aiMessage = { 
        id: uuidv4(),
        sender: 'ai', 
        text: data.response,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error communicating with chat API:', error);
      const errorMessage = { 
        id: uuidv4(),
        sender: 'ai', 
        text: 'Sorry, something went wrong. Please try again later.',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false); 
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className={styles.chatWidget}>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className={styles.chatWindow}
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 50 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          >
            <div className={styles.header}>
              <div className={styles.headerLeft}>
                <FaRobot className={styles.icon} />
                <span>NeXuS-AI Chat</span>
              </div>
              <div className={styles.headerRight}>
                <button 
                  onClick={toggleDarkMode} 
                  className={styles.iconButton}
                  data-tooltip-id="dark-mode-tooltip"
                  data-tooltip-content={darkMode ? "Light Mode" : "Dark Mode"}
                >
                  {darkMode ? <AiOutlineBulb /> : <AiOutlineMoon />}
                </button>
                <button 
                  onClick={toggleChat} 
                  className={styles.closeButton}
                  aria-label="Close chat"
                >
                  <AiOutlineClose />
                </button>
              </div>
              <Tooltip id="dark-mode-tooltip" place="top" />
            </div>
            <div className={styles.messages}>
              {messages.map((msg) => (
                <motion.div
                  key={msg.id}
                  className={
                    msg.sender === 'user'
                      ? `${styles.message} ${styles.userMessage}`
                      : `${styles.message} ${styles.aiMessage}`
                  }
                  initial={{ opacity: 0, x: msg.sender === 'user' ? 100 : -100 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className={styles.avatar}>
                    {msg.sender === 'user' ? <FaUserCircle /> : <FaRobot />}
                  </div>
                  <div className={styles.messageContent}>
                    <p>{msg.text}</p>
                    <span className={styles.timestamp}>
                      {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </motion.div>
              ))}
              {loading && (
                <motion.div
                  className={styles.loadingMessage}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
         <AiOutlineLoading3Quarters className={`${styles.spinner} text-white dark:text-blue-300`} />
<span className="text-white dark:text-blue-300">AI is typing...</span>


                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </div>
            <div className={styles.inputArea}>
              <button className={styles.messageButton} aria-label="Send message">
                <AiOutlineMessage /> {/* Replaced AiOutlineSmile with AiOutlineMessage */}
              </button>
              <textarea
                placeholder="Type your message..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                className={styles.input}
                rows={1}
              />
              {input && (
                <button 
                  onClick={() => setInput('')} 
                  className={styles.clearButton}
                  aria-label="Clear input"
                >
                  &times;
                </button>
              )}
              <button 
                onClick={sendMessage} 
                className={styles.sendButton}
                disabled={loading}
                aria-label="Send message"
              >
                <AiOutlineSend />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <button onClick={toggleChat} className={styles.chatButton} aria-label="Open chat">
        💬
      </button>
    </div>
  );
};

export default ChatWidget;
